import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import type {
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
  GridRowParams,
} from "@mui/x-data-grid";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import type { JobResponse } from "@taxassist/api-client";
import { StatusBadge } from "../jobs/StatusBadge";
import { JobDetailsDrawer } from "../jobs/JobDetailsDrawer";
import { formatJobDate, formatJobReference } from "../../utils/jobUtils";
import type { SortConfig } from "../../pages/jobs/ListJobsView";

interface JobsDataTableProps {
  jobs: JobResponse[];
  isLoading: boolean;
  sortConfig: SortConfig;
  onSortChange: (sort: SortConfig) => void;
  selectedJobs: Set<string>;
  onSelectionChange: (selection: Set<string>) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onJobSelected?: (jobId: string, firmId: string) => void;
  selectedJobId?: string; // Currently selected job for details panel
}

export function JobsDataTable({
  jobs,
  isLoading,
  sortConfig,
  onSortChange,
  selectedJobs,
  onSelectionChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onJobSelected,
  selectedJobId,
}: JobsDataTableProps) {
  const theme = useTheme();
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState<
    string | null
  >(null);

  const apiRef = useGridApiRef();

  useEffect(() => {
    setTimeout(() => {
      if (apiRef.current && selectedJobId && jobs) {
        const idx = jobs.findIndex((job) => job.jobId === selectedJobId)
        if (idx > -1) {
          apiRef.current.scrollToIndexes({ rowIndex: idx, colIndex: 0 });
        }
      }
    }, 0)
  }, [apiRef, jobs, selectedJobId])

  // Local pagination state for DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: page,
    pageSize: pageSize,
  });

  // Flag to track if pagination change came from DataGrid (to prevent sync loop)
  const isInternalChangeRef = useRef(false);

  // Only sync local state with props when they change externally (e.g., filter reset)
  // Skip sync if the change came from DataGrid itself
  useEffect(() => {
    if (!isInternalChangeRef.current) {
      setPaginationModel({ page, pageSize });
    }
    isInternalChangeRef.current = false;
  }, [page, pageSize]);

  // Memoize pagination change handler to prevent unnecessary re-renders
  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      isInternalChangeRef.current = true;
      setPaginationModel(model);
      onPageChange(model.page);
      onPageSizeChange(model.pageSize);
    },
    [onPageChange, onPageSizeChange],
  );

  // Transform jobs data for DataGrid
  const rows = useMemo(() => {
    if (!jobs || !Array.isArray(jobs)) {
      return [];
    }
    return jobs.map((job) => ({
      id: job.jobId,
      reference: formatJobReference(job),
      clientName: job.clientName || "Unknown Client",
      clientNumber: job.clientNumber || job.jobId.substring(0, 8), // Use clientNumber or first 8 chars of jobId
      status: job.status,
      filingType: job.filingType || "N/A",
      taxYear: job.taxYear || "No Year",
      createdAt: job.createdAt,
      updatedAt: job.updatedAt,
      fileCount: job.uploadedFiles?.length || 0,
      jobData: job, // Store full job data for actions
    }));
  }, [jobs]);

  // Handle row selection
  const handleSelectionModelChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      if (onSelectionChange) {
        onSelectionChange(new Set(newSelection as unknown as string[]));
      }
    },
    [onSelectionChange],
  );

  // Handle sorting
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        onSortChange({
          field: field as keyof JobResponse,
          direction: sort === "asc" ? "asc" : "desc",
        });
      }
    },
    [onSortChange],
  );

  // Handle job actions
  const handleViewJob = useCallback((job: JobResponse) => {
    setSelectedJobForDetails(job.jobId);
    setDetailsDrawerOpen(true);
  }, []);

  // Handle row click
  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      const job = params.row.jobData as JobResponse;
      if (onJobSelected) {
        onJobSelected(job.jobId, job.firmId);
      } else {
        // Fallback to internal handler if no external handler provided
        handleViewJob(job);
      }
    },
    [onJobSelected, handleViewJob],
  );

  const handleCloseDetailsDrawer = useCallback(() => {
    setDetailsDrawerOpen(false);
    setSelectedJobForDetails(null);
  }, []);

  // Column definitions
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "clientName",
        headerName: "Client",
        flex: 1.5,
        minWidth: 200,
        maxWidth: 400,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box>
            <Box
              sx={{ fontWeight: 500, fontSize: "0.875rem", lineHeight: 1.2 }}
            >
              {params.value}
            </Box>
            <Box
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                lineHeight: 1.2,
                mt: 0.25,
              }}
            >
              {params.row.clientNumber}
            </Box>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 140,
        maxWidth: 240,
        headerAlign: "left",
        align: "left",
        sortable: true,
        renderCell: (params) => (
          <StatusBadge status={params.value} job={params.row} />
        ),
      },
      {
        field: "fileCount",
        headerName: "Files",
        flex: 0.5,
        minWidth: 80,
        maxWidth: 120,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => <Box>{params.value}</Box>,
      },
      {
        field: "taxYear",
        headerName: "Tax Year",
        flex: 0.6,
        minWidth: 100,
        maxWidth: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            {params.value}
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 1,
        minWidth: 100,
        maxWidth: 200,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.875rem" }}>{formatJobDate(params.value)}</Box>
        ),
      },
    ],
    [],
  );

  // Sort model for DataGrid
  const sortModel = useMemo(
    () => [
      {
        field: sortConfig.field,
        sort: sortConfig.direction,
      },
    ],
    [sortConfig],
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={isLoading}
        checkboxSelection={false}
        disableRowSelectionOnClick
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
        pageSizeOptions={[10, 25, 50, 100]}
        density="standard"
        autoHeight={false}
        disableColumnResize={false}
        disableColumnMenu={false}
        getRowClassName={(params) =>
          params.id === selectedJobId ? "Mui-selected" : ""
        }
        paginationMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowHeight={72}
        sx={{
          border: "none",
          flex: 1,
          "& .MuiDataGrid-main": {
            borderRadius: 0,
          },
          "&& .MuiDataGrid-filler": {
            backgroundColor: "#fff",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#fff",
            borderBottom: "1px solid #E7E7E7",
            minHeight: "56px !important",
            "& .MuiDataGrid-columnHeader": {
              fontWeight: 600,
              backgroundColor: "#fff",
              fontSize: "0.875rem",
              color: theme.palette.text.primary,
              padding: "16px",
            },
          },
          "& .MuiDataGrid-scrollbarFiller": {
            backgroundColor: "#FFF !important",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
          "& .MuiDataGrid-row": {
            minHeight: "72px !important",
            borderBottom: "1px solid #E7E7E7",
            cursor: "pointer",
            backgroundColor: "#FFF",
            "&:hover": {
              backgroundColor: "#F5F5F5",
            },
            "&.Mui-selected": {
              backgroundColor: "#EDF7FF !important", // Light blue for selected row
              "&:hover": {
                backgroundColor: "#E3F2FD !important",
              },
            },
            "&:last-child": {
              borderBottom: "none",
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            padding: "16px",
            fontSize: "0.875rem",
            "&:focus": {
              outline: "none !important",
            },
            "&:focus-within": {
              outline: "none !important",
            },
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #E7E7E7",
            backgroundColor: "#FFFFFF",
            minHeight: "52px",
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  fontSize: "3rem",
                  opacity: 0.3,
                }}
              >
                📄
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Box sx={{ fontWeight: 500, mb: 1 }}>No jobs found</Box>
                <Box sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                  Try adjusting your filters or create a new job
                </Box>
              </Box>
            </Box>
          ),
          loadingOverlay: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              Loading jobs...
            </Box>
          ),
        }}
      />

      {/* Legacy Job Details Drawer - only shows if no external handler provided */}
      {!onJobSelected && selectedJobForDetails && (
        <JobDetailsDrawer
          open={detailsDrawerOpen}
          onClose={handleCloseDetailsDrawer}
          jobId={selectedJobForDetails}
        />
      )}
    </Box>
  );
}
